import { __decorate, __metadata } from 'tslib';
import { trigger, transition, group, useAnimation } from '@angular/animations';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Injectable, EventEmitter, Component, ChangeDetectionStrategy, ViewEncapsulation, Input, Output, inject, DestroyRef, Renderer2, numberAttribute, NgModule } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { WindowRef, CoreModule } from '@odx/angular';
import { expand, fadeIn, slideInRight, collapse } from '@odx/angular/animations';
import { ChipComponent } from '@odx/angular/components/chip';
import { HeaderComponent } from '@odx/angular/components/header';
import { deepmerge, CSSModifier, CSSComponent } from '@odx/angular/internal';
import { createConfigTokens, Queue, getUniqueId, untilDestroyed, injectElement, setAttribute, px, deferFn } from '@odx/angular/utils';
import { timer, tap, map } from 'rxjs';
import { DynamicViewDirective } from '@odx/angular/cdk/dynamic-view';
import { ActionGroupComponent } from '@odx/angular/components/action-group';
import { ButtonComponent } from '@odx/angular/components/button';
import { IconComponent } from '@odx/angular/components/icon';

/**
 * Utility functions generated by `createConfigTokens` to handle injection and provision of `ToastConfig`.
 * These include tokens and functions to access and provide the default configuration for toasts.
 *
 * `ToastDefaultConfig` provides the default settings for toasts.
 * `ToastConfig` is a token that can be used to inject toast configuration settings.
 * `injectToastConfig` is a function that retrieves the current toast configuration.
 * `provideToastConfig` is a function used to specify a custom configuration for toasts.
 *
 * @example
 * ```ts
 * // In module providers:
 * providers: [
 *  provideToastConfig({ // ... global module configuration }),
 * ]
 *
 * // In a component or service:
 * constructor(@Inject(ToastConfig) private readonly config: ToastConfig) {}
 *
 * // Or using the `injectToastConfig` function:
 * @Component({ ... })
 * export class MyComponent {
 *  private readonly config = injectToastConfig();
 * }
 * ```
 */
const { ToastDefaultConfig, ToastConfig, injectToastConfig, provideToastConfig } = createConfigTokens('Toast', '@odx/angular/components/toast', {
    dismissable: true,
    duration: 0,
});

/**
 * ToastService provides a centralized management system for toasts within an application.
 * It handles the creation, updates, and dismissal of toasts based on a FIFO (first-in, first-out) queue.
 * This service integrates custom toast options with default configurations and supports reactive updates through observables.
 */
class ToastService {
    constructor() {
        this.queue = new Queue();
        this.defaultOptions = injectToastConfig();
        /**
         * Observable stream of active toasts.
         *
         * @emits {ToastRef[]} The current list of active toasts.
         */
        this.toasts$ = this.queue.value$;
        /**
         * Observable that emits when any update occurs in the toast queue.
         *
         * @emits {ToastRef[]} The updated list of active toasts.
         */
        this.onUpdate$ = this.queue.onUpdate$;
        /**
         * Observable that emits when a toast is added.
         *
         * @emits {ToastRef} The newly added toast.
         */
        this.onToastAdd$ = this.queue.onAdd$;
        /**
         * Observable that emits when a toast is updated.
         *
         * @emits {ToastRef} The updated toast.
         */
        this.onToastUpdate$ = this.queue.onUpdate$;
        /**
         * Observable that emits when a toast is removed.
         *
         * @emits {ToastRef} The removed toast.
         */
        this.onToastRemove$ = this.queue.onRemove$;
        /**
         * @deprecated Use `toasts$` instead
         */
        this.queue$ = this.toasts$;
        /**
         * @deprecated Use `onToastAdd$` instead
         */
        this.onAdd$ = this.onToastAdd$;
        /**
         * @deprecated Use `onToastRemove$` instead
         */
        this.onRemove$ = this.onToastRemove$;
    }
    /**
     * @deprecated Use `create` instead
     */
    add(item, options = {}) {
        return this.create(item, options);
    }
    /**
     * Creates and queues a new toast based on the provided item and options.
     * Merges given options with default configurations.
     *
     * @param {Toast} item The toast item to create.
     * @param {Partial<ToastOptions>} options Optional additional options for the toast.
     * @returns {ToastRef} The reference to the newly created toast.
     */
    create(item, options = {}) {
        return this.queue.add({
            id: item.id ?? getUniqueId('odx-toast'),
            item,
            options: deepmerge(this.defaultOptions, options),
        });
    }
    /**
     * @deprecated Use `dismiss` instead
     */
    remove(refOrId) {
        return this.dismiss(refOrId);
    }
    /**
     * Dismisses a specific toast identified by a reference or ID.
     *
     * @param {ToastRef | ToastRef['id']} refOrId The reference or ID of the toast to dismiss.
     * @returns {ToastRef | null} The reference to the dismissed toast, or null if not found.
     */
    dismiss(refOrId) {
        return this.queue.remove(refOrId);
    }
    /**
     * @deprecated Use `dismissAll` instead
     */
    clear() {
        return this.dismissAll();
    }
    /**
     * Dismisses all toasts currently being displayed.
     */
    dismissAll() {
        this.queue.clear();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

const ToastVariant = {
    DEFAULT: 'default',
    WARNING: 'warning',
    DANGER: 'danger',
    SUCCESS: 'success',
};

/**
 * A component that represents a single toast notification. This component is responsible for displaying
 * message content, handling toast actions, and managing the automatic dismissal of the toast based on a duration.
 * It supports dynamic content for descriptions and can show an optional set of actions.
 */
let ToastItemComponent = class ToastItemComponent {
    constructor() {
        this.takeUntilDestroyed = untilDestroyed();
        this.element = injectElement();
        /**
         * The variant of the toast which influences the icon and styling.
         *
         * @type {ToastVariant | null}
         * @default ToastVariant.DEFAULT
         */
        this.variant = ToastVariant.DEFAULT;
        /**
         * Emits an event when the toast is dismissed, either through action or automatically.
         *
         * @emits {void}
         */
        this.dismiss = new EventEmitter();
    }
    /**
     * Determines the icon to display based on the toast's variant.
     * Returns a string representing the icon class.
     *
     * @returns {string} - The icon class name.
     * @default 'info'
     */
    get icon() {
        switch (this.variant) {
            case ToastVariant.WARNING:
                return 'warning';
            case ToastVariant.DANGER:
                return 'error';
            case ToastVariant.SUCCESS:
                return 'check';
            default:
                return 'info';
        }
    }
    ngOnInit() {
        const { duration } = this.options;
        if (duration > 0) {
            timer(duration)
                .pipe(this.takeUntilDestroyed(), tap(() => this.onDismiss()))
                .subscribe();
        }
    }
    onDismiss() {
        this.dismiss.next();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastItemComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: ToastItemComponent, isStandalone: true, selector: "odx-toast", inputs: { id: "id", title: "title", description: "description", descriptionContext: "descriptionContext", variant: "variant", actions: "actions", options: "options" }, outputs: { dismiss: "dismiss" }, host: { properties: { "attr.id": "id" } }, ngImport: i0, template: "<div class=\"odx-toast-item__content\">\n  <odx-icon [name]=\"icon\" iconSet=\"core\" />\n  <div class=\"odx-toast-item__text-content\">\n    <p class=\"odx-toast-item__title\">\n      <ng-template [odxDynamicView]=\"title\" />\n    </p>\n    @if (description) {\n      <p class=\"odx-toast-item__description\">\n        <ng-template [odxDynamicView]=\"description\" [odxDynamicViewContext]=\"descriptionContext\" />\n      </p>\n    }\n  </div>\n  @if (options.dismissable) {\n    <button odxButton class=\"odx-toast-item__close\" (click)=\"onDismiss()\"><odx-icon name=\"close\" iconSet=\"core\" /></button>\n  }\n</div>\n@if (!!actions?.length) {\n  <odx-action-group class=\"odx-toast-item__buttons\">\n    @for (action of actions; track $index) {\n      <button odxButton (click)=\"action.action()\">\n        @if (action.leftIcon) {\n          <odx-icon [name]=\"action.leftIcon\" iconSet=\"core\" alignLeft />\n        }\n        <ng-template [odxDynamicView]=\"action.label\" />\n        @if (action.rightIcon) {\n          <odx-icon [name]=\"action.rightIcon\" iconSet=\"core\" alignRight />\n        }\n      </button>\n    }\n  </odx-action-group>\n}\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "component", type: IconComponent, selector: "odx-icon", inputs: ["inline", "size", "name", "iconSet", "identifier"] }, { kind: "component", type: ButtonComponent, selector: "button[odxButton], a[odxButton]", inputs: ["variant", "size"] }, { kind: "directive", type: DynamicViewDirective, selector: "ng-template[odxDynamicView]", inputs: ["odxDynamicView", "odxDynamicViewInjector", "odxDynamicViewContext"] }, { kind: "component", type: ActionGroupComponent, selector: "odx-action-group", inputs: ["reverse"] }], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
__decorate([
    CSSModifier(),
    __metadata("design:type", Object)
], ToastItemComponent.prototype, "variant", void 0);
ToastItemComponent = __decorate([
    CSSComponent('toast-item')
], ToastItemComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastItemComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-toast', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, imports: [CommonModule, IconComponent, ButtonComponent, DynamicViewDirective, ActionGroupComponent], host: {
                        '[attr.id]': 'id',
                    }, template: "<div class=\"odx-toast-item__content\">\n  <odx-icon [name]=\"icon\" iconSet=\"core\" />\n  <div class=\"odx-toast-item__text-content\">\n    <p class=\"odx-toast-item__title\">\n      <ng-template [odxDynamicView]=\"title\" />\n    </p>\n    @if (description) {\n      <p class=\"odx-toast-item__description\">\n        <ng-template [odxDynamicView]=\"description\" [odxDynamicViewContext]=\"descriptionContext\" />\n      </p>\n    }\n  </div>\n  @if (options.dismissable) {\n    <button odxButton class=\"odx-toast-item__close\" (click)=\"onDismiss()\"><odx-icon name=\"close\" iconSet=\"core\" /></button>\n  }\n</div>\n@if (!!actions?.length) {\n  <odx-action-group class=\"odx-toast-item__buttons\">\n    @for (action of actions; track $index) {\n      <button odxButton (click)=\"action.action()\">\n        @if (action.leftIcon) {\n          <odx-icon [name]=\"action.leftIcon\" iconSet=\"core\" alignLeft />\n        }\n        <ng-template [odxDynamicView]=\"action.label\" />\n        @if (action.rightIcon) {\n          <odx-icon [name]=\"action.rightIcon\" iconSet=\"core\" alignRight />\n        }\n      </button>\n    }\n  </odx-action-group>\n}\n" }]
        }], propDecorators: { id: [{
                type: Input
            }], title: [{
                type: Input
            }], description: [{
                type: Input
            }], descriptionContext: [{
                type: Input
            }], variant: [{
                type: Input
            }], actions: [{
                type: Input
            }], options: [{
                type: Input
            }], dismiss: [{
                type: Output
            }] } });

var ToastContainerComponent_1;
const TOAST_ANIMATION_DELAY = '125ms';
/**
 * Represents a container component for displaying toast notifications. This component manages and arranges toast messages,
 * handling animations for entering and leaving toasts. It ensures that only a limited number of toasts are shown at the same time,
 * based on the `maxItems` input. The container listens for toast events from the `ToastService` to update its list of toasts dynamically.
 *
 * The component uses a group of animations to slide toasts in and out smoothly and also provides an output for when a toast is dismissed.
 * It's designed to integrate with a single instance per application, ensuring no more than one toast container is initialized.
 */
let ToastContainerComponent = class ToastContainerComponent {
    constructor() {
        this.destroyRef = inject(DestroyRef);
        this.windowRef = inject(WindowRef);
        this.renderer = inject(Renderer2);
        this.toastService = inject(ToastService);
        this.element = injectElement();
        this.items$ = this.toastService.toasts$.pipe(map((refs) => refs.slice(0, this.maxItems)));
        this.hiddenToasts$ = this.toastService.toasts$.pipe(map((refs) => Math.max(0, refs.length - this.maxItems)));
        /**
         * Maximum number of toasts that can be displayed at once.
         *
         * @type {number}
         * @default 3
         */
        this.maxItems = 3;
        /**
         * Emits an event when a toast is dismissed.
         *
         * @emits {ToastRef} - The toast reference that was dismissed.
         */
        this.dismiss = this.toastService.onToastRemove$;
    }
    static { ToastContainerComponent_1 = this; }
    static { this.INITIALIZED = false; }
    ngAfterViewInit() {
        setAttribute(this.element.nativeElement, 'popover', 'manual');
        if (ToastContainerComponent_1.INITIALIZED) {
            throw new Error('Only one toast container per application allowed');
        }
        ToastContainerComponent_1.INITIALIZED = true;
        const headerElement = this.windowRef.queryByComponent(HeaderComponent);
        if (headerElement) {
            this.renderer.setStyle(this.element.nativeElement, 'top', px(headerElement.offsetHeight));
        }
        this.items$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.updatePopverPosition());
    }
    ngOnDestroy() {
        ToastContainerComponent_1.INITIALIZED = false;
    }
    onDismiss(itemRef) {
        this.toastService.dismiss(itemRef);
    }
    updatePopverPosition() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.element.nativeElement?.hidePopover?.();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        deferFn(() => this.element.nativeElement?.showPopover?.());
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: ToastContainerComponent, isStandalone: true, selector: "odx-toast-container", inputs: { maxItems: ["maxItems", "maxItems", numberAttribute] }, outputs: { dismiss: "dismiss" }, ngImport: i0, template: "@for (toastRef of items$ | async; track toastRef.id) {\n  <odx-toast\n    @slide\n    [id]=\"toastRef.id\"\n    [title]=\"toastRef.item.title\"\n    [description]=\"toastRef.item.description\"\n    [descriptionContext]=\"toastRef.item.descriptionContext\"\n    [actions]=\"toastRef.item.actions ?? []\"\n    [variant]=\"toastRef.item.variant\"\n    [options]=\"toastRef.options\"\n    (dismiss)=\"onDismiss(toastRef)\"\n  />\n}\n@if (hiddenToasts$ | async; as more) {\n  <odx-chip class=\"odx-toast-container__counter\">{{ more }} more Notifications</odx-chip>\n}\n", dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "pipe", type: i1.AsyncPipe, name: "async" }, { kind: "component", type: ChipComponent, selector: "odx-chip", inputs: ["removable", "size", "variant"], outputs: ["remove"] }, { kind: "component", type: ToastItemComponent, selector: "odx-toast", inputs: ["id", "title", "description", "descriptionContext", "variant", "actions", "options"], outputs: ["dismiss"] }], animations: [
            trigger('slide', [
                transition(':enter', group([
                    useAnimation(expand),
                    useAnimation(fadeIn(), { params: { delay: TOAST_ANIMATION_DELAY } }),
                    useAnimation(slideInRight, { params: { delay: TOAST_ANIMATION_DELAY } }),
                ])),
                transition(':leave', group([useAnimation(collapse)])),
            ]),
        ], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
ToastContainerComponent = ToastContainerComponent_1 = __decorate([
    CSSComponent('toast-container')
], ToastContainerComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'odx-toast-container', standalone: true, changeDetection: ChangeDetectionStrategy.OnPush, encapsulation: ViewEncapsulation.None, imports: [CommonModule, ChipComponent, ToastItemComponent], animations: [
                        trigger('slide', [
                            transition(':enter', group([
                                useAnimation(expand),
                                useAnimation(fadeIn(), { params: { delay: TOAST_ANIMATION_DELAY } }),
                                useAnimation(slideInRight, { params: { delay: TOAST_ANIMATION_DELAY } }),
                            ])),
                            transition(':leave', group([useAnimation(collapse)])),
                        ]),
                    ], template: "@for (toastRef of items$ | async; track toastRef.id) {\n  <odx-toast\n    @slide\n    [id]=\"toastRef.id\"\n    [title]=\"toastRef.item.title\"\n    [description]=\"toastRef.item.description\"\n    [descriptionContext]=\"toastRef.item.descriptionContext\"\n    [actions]=\"toastRef.item.actions ?? []\"\n    [variant]=\"toastRef.item.variant\"\n    [options]=\"toastRef.options\"\n    (dismiss)=\"onDismiss(toastRef)\"\n  />\n}\n@if (hiddenToasts$ | async; as more) {\n  <odx-chip class=\"odx-toast-container__counter\">{{ more }} more Notifications</odx-chip>\n}\n" }]
        }], propDecorators: { maxItems: [{
                type: Input,
                args: [{ transform: numberAttribute }]
            }], dismiss: [{
                type: Output
            }] } });

const modules = [ToastContainerComponent, ToastItemComponent];
class ToastModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: ToastModule, imports: [ToastContainerComponent, ToastItemComponent], exports: [CoreModule, ToastContainerComponent, ToastItemComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastModule, imports: [modules, CoreModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ToastModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: modules,
                    exports: [CoreModule, ...modules],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { ToastConfig, ToastContainerComponent, ToastDefaultConfig, ToastItemComponent, ToastModule, ToastService, ToastVariant, injectToastConfig, provideToastConfig };
